import Service, { service } from '@ember/service';
import { saveRecord } from 'tio-data/requests/save-record';
import type { Store } from '@warp-drive/core';
import type { IntlService } from 'ember-intl';
import type DemoModeService from 'tio-employee/services/demo-mode';
import type ImpersonationService from 'tio-employee/services/impersonation';
import type SessionContextService from 'tio-employee/services/session-context';

export const SUPPORTED_LOCALES = ['en-us', 'fr-ca', 'es-mx'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

const STORAGE_KEY = 'locale';

export function isSupportedLocale(value: unknown): value is SupportedLocale {
  return SUPPORTED_LOCALES.includes(value as SupportedLocale);
}

/** The signed-in person's saved locale, kept in step with the browser override boot reads. */
export default class LocalePreferenceService extends Service {
  @service declare demoMode: DemoModeService;
  @service declare impersonation: ImpersonationService;
  @service declare intl: IntlService;
  @service declare sessionContext: SessionContextService;
  @service declare store: Store;

  /** Null until the person chooses one, and whenever `savesToAccount` is false. */
  get saved() {
    if (!this.savesToAccount) return null;
    const locale = this.sessionContext.person.locale;
    return isSupportedLocale(locale) ? locale : null;
  }

  /**
   * False while support impersonates the employee, on the demo account, and when the session has
   * no saved person (saving that placeholder would create one): choices stay in this browser.
   */
  get savesToAccount() {
    const person = this.sessionContext.person;
    return (
      Boolean(person) &&
      !person.isNew &&
      !this.impersonation.isImpersonated &&
      !this.demoMode.isDemoSessionUser
    );
  }

  /** Most preferred first. */
  get browserLanguages(): readonly string[] {
    return navigator.languages?.length ? navigator.languages : [navigator.language];
  }

  get browserListsFrench() {
    return this.browserLanguages.some((language) => language.toLowerCase().split('-')[0] === 'fr');
  }

  /**
   * Saves `locale` on the person (null clears it) when `savesToAccount`, then makes it this
   * browser's override. Does not switch the app; see `reloadUnlessShowing`.
   *
   * @returns false when storage refused the override, so a reload would not switch the language
   * @throws when the save fails, after rolling the person back and leaving the override alone
   */
  save = async (locale: SupportedLocale | null) => {
    if (this.savesToAccount) {
      const person = this.sessionContext.person;
      person.locale = locale;
      try {
        await saveRecord(this.store, person);
      } catch (error) {
        person.rollbackAttributes();
        throw error;
      }
    }
    return this.#remember(locale);
  };

  /** Reloads when the app shows another locale; translations are loaded once, at boot. */
  reloadUnlessShowing = (locale: SupportedLocale) => {
    if (this.intl.primaryLocale !== locale) this.reload();
  };

  /**
   * After sign-in, makes this browser use the saved locale, reloading once if boot chose another.
   * `saved` is null while `savesToAccount` is false, so support keep their own language.
   */
  applySaved = () => {
    const saved = this.saved;
    if (saved && this.#remember(saved)) this.reloadUnlessShowing(saved);
  };

  reload = () => {
    window.location.reload();
  };

  // False when storage refused the write, so a reload would not change what boot reads.
  #remember(locale: SupportedLocale | null) {
    try {
      if (locale) localStorage.setItem(STORAGE_KEY, locale);
      else localStorage.removeItem(STORAGE_KEY);
      return localStorage.getItem(STORAGE_KEY) === locale;
    } catch {
      return false;
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    'locale-preference': LocalePreferenceService;
  }
}
